import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import {
  BankOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  CommentOutlined,
  FileExcelOutlined,
  FileExclamationOutlined,
  FilePdfOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  FolderOpenOutlined,
  IdcardOutlined,
  MoreOutlined,
  RightOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { Button, Dropdown } from 'antd';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { escapeRegExp } from 'src/util/notificationFormatting';
import type { NotificationType } from 'src/util/notificationConfig';

type NotificationPriority = 'normal' | 'high';
type DetailCardVariant = 'expired' | 'compliance-check';

// These backend events already represent quoted user text.
const QUOTED_UI_TYPES = new Set<NotificationType>([
  'BINDER_COMMENT',
  'BINDER_COMMENT_MENTION',
  'COMMENT',
  'COMMENT_MENTION',
]);

const getNotificationIcon = (
  triggerType: NotificationType,
  iconUrl?: string
): ReactNode => {
  if (iconUrl) {
    return (
      <img
        src={iconUrl}
        alt=""
        className="h-5 w-5 rounded-sm object-contain"
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).style.display = 'none';
        }}
      />
    );
  }
  switch (triggerType) {
    case 'BINDER_COMMENT_MENTION':
    case 'COMMENT_MENTION':
      return <span className="text-lg leading-none">@</span>;
    case 'COMMENT':
      return <CommentOutlined />;
    case 'COMPANY_FEEDBACK_RECEIVED':
      return <CommentOutlined />;
    case 'NEWS_ARTICLE_FEEDBACK_RECEIVED':
      return <FileTextOutlined />;
    case 'BINDER_COMMENT':
      return <FolderOpenOutlined />;
    case 'COMPANY_REGISTRATION_UPDATE':
    case 'BATCH_REGISTRATION_UPDATE':
      return <CheckCircleOutlined />;
    case 'COMPLIANCE_CHECK_UPDATE':
    case 'BATCH_COMPLIANCE_CHECK_UPDATE':
      return <SafetyCertificateOutlined />;
    case 'MISSING_COMPANY_INVESTIGATION_UPDATE':
      return <FileSearchOutlined />;
    case 'USER_DETAILS_UPDATE':
      return <IdcardOutlined />;
    case 'EXPORT_UPDATE':
      return <FileTextOutlined />;
    case 'SUPPORT_REQUEST_UPDATE':
      return <CommentOutlined />;
    default:
      return <FolderOpenOutlined />;
  }
};

const getDetailCardIcon = (
  triggerType: NotificationType,
  iconUrl?: string,
  exportFileType?: string,
  detailCardVariant?: DetailCardVariant
): ReactNode => {
  if (detailCardVariant === 'expired') {
    return <FileExclamationOutlined className="text-[18px] text-brand-gray" />;
  }

  if (detailCardVariant === 'compliance-check') {
    return (
      <SafetyCertificateOutlined className="text-[18px] text-brand-gray" />
    );
  }

  if (iconUrl) {
    return (
      <img
        src={iconUrl}
        alt=""
        className="h-[18px] w-[18px] rounded-sm object-contain"
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).style.display = 'none';
        }}
      />
    );
  }
  switch (triggerType) {
    case 'EXPORT_UPDATE':
      return getExportIcon(exportFileType, 'text-[18px] text-brand-gray');
    case 'NEWS_ARTICLE_FEEDBACK_RECEIVED':
      return <FileTextOutlined className="text-[18px] text-brand-gray" />;
    case 'COMMENT':
    case 'COMMENT_MENTION':
    case 'COMPANY_REGISTRATION_UPDATE':
    case 'COMPLIANCE_CHECK_UPDATE':
    case 'COMPANY_FEEDBACK_RECEIVED':
    case 'MISSING_COMPANY_INVESTIGATION_UPDATE':
    case 'BATCH_REGISTRATION_UPDATE':
    case 'BATCH_COMPLIANCE_CHECK_UPDATE':
      return <BankOutlined className="text-[18px] text-brand-gray" />;
    case 'USER_DETAILS_UPDATE':
      return <IdcardOutlined className="text-[18px] text-brand-gray" />;
    case 'SUPPORT_REQUEST_UPDATE':
      return <CommentOutlined className="text-[18px] text-brand-gray" />;
    default:
      return <FolderOpenOutlined className="text-[18px] text-brand-gray" />;
  }
};

const getExportIcon = (fileType?: string, className?: string): ReactNode => {
  const normalizedFileType = fileType?.toLowerCase();

  if (normalizedFileType === 'excel' || normalizedFileType === 'xlsx') {
    return <FileExcelOutlined className={className} />;
  }

  if (normalizedFileType === 'pdf') {
    return <FilePdfOutlined className={className} />;
  }

  return <FileTextOutlined className={className} />;
};

type InboxNotificationProps = {
  priority: NotificationPriority;
  triggerType: NotificationType;
  typeLabel: string;
  timeAgo: string;
  message: ReactNode;
  quoteText?: string;
  quoteHighlightValues?: string[];
  detailCardLabel?: string;
  detailCardVariant?: DetailCardVariant;
  progress?: number;
  progressDetail?: string;
  isUpdated?: boolean;
  originalTimeAgo?: string;
  isRead?: boolean;
  onToggleRead?: () => void;
  hideReadToggle?: boolean;
  hideDetailCard?: boolean;
  showPrimaryActionWithDetailCard?: boolean;
  onNotificationClick?: () => void;

  primaryActionLabel?: string;
  secondaryActionLabel?: string;
  onPrimaryAction?: () => void;
  onSecondaryAction?: () => void;
  isMuted?: boolean;
  muteLabel?: string;
  onMuteType?: () => void;
  onUnmuteType?: () => void;
  exportFileType?: string;
  iconUrl?: string;
};

const InboxNotification = ({
  priority,
  triggerType,
  typeLabel,
  timeAgo,
  message,
  quoteText,
  quoteHighlightValues,
  detailCardLabel,
  detailCardVariant,
  progress,
  progressDetail,
  isUpdated = false,
  originalTimeAgo,
  isRead = false,
  onToggleRead,
  hideReadToggle = false,
  hideDetailCard = false,
  showPrimaryActionWithDetailCard = false,
  onNotificationClick,
  primaryActionLabel,
  secondaryActionLabel,
  onPrimaryAction,
  onSecondaryAction,
  isMuted = false,
  muteLabel,
  onMuteType,
  onUnmuteType,
  exportFileType,
  iconUrl,
}: InboxNotificationProps) => {
  const { t } = useTranslation('inbox');

  const shouldAddUiQuotes = QUOTED_UI_TYPES.has(triggerType);
  const showDetailCard = Boolean(detailCardLabel && !hideDetailCard);
  const detailCardIsExpired = detailCardVariant === 'expired';
  const showDetailAction = Boolean(
    showDetailCard && onPrimaryAction && !detailCardIsExpired
  );
  const translatedTypeLabel = t(typeLabel);
  const translatedMuteLabel = muteLabel ?? translatedTypeLabel;

  // Highlight exact template values first, then fall back to @mentions.
  const renderTemplateValueHighlight = (text: string): ReactNode => {
    const normalizeHighlightValue = (value: string) =>
      value
        .replace(/^["“]|["”]$/g, '')
        .trim()
        .toLowerCase();
    const normalizedText = normalizeHighlightValue(text);
    const values = Array.from(
      new Set(
        (quoteHighlightValues ?? [])
          .map((value) => value?.trim())
          .filter((value): value is string => Boolean(value))
          .filter((value) => normalizeHighlightValue(value) !== normalizedText)
      )
    );

    if (values.length === 0) {
      return renderMentionHighlight(text);
    }

    const normalizedValues = new Set(
      values.map((value) => value.toLowerCase())
    );
    // Capture an optional leading @ so a mentioned name highlights together
    // with its @ (e.g. "@Donovan Tjokrodimedjo").
    const pattern = new RegExp(
      `(@?(?:${values.map((value) => escapeRegExp(value)).join('|')}))`,
      'gi'
    );
    const parts = text.split(pattern);

    return parts.map((part, index) =>
      normalizedValues.has(part.replace(/^@/, '').toLowerCase()) ? (
        <span
          key={`${part}-${index}`}
          className="inbox-notification__highlight"
        >
          {part}
        </span>
      ) : (
        <span key={`${part}-${index}`}>{renderMentionHighlight(part)}</span>
      )
    );
  };

  // Include the first name after an @mention in the highlight.
  const renderMentionHighlight = (text: string): ReactNode => {
    const parts = text.split(/(\s+)/g);
    let highlightNextWord = false;

    return parts.map((part, idx) => {
      if (/^@[^\s]+$/.test(part)) {
        highlightNextWord = true;
        return (
          <span key={idx} className="inbox-notification__highlight">
            {part}
          </span>
        );
      }

      if (highlightNextWord && part.trim().length > 0) {
        highlightNextWord = false;
        return (
          <span key={idx} className="inbox-notification__highlight">
            {part}
          </span>
        );
      }

      return part;
    });
  };

  const renderQuoteText = (text: string): ReactNode =>
    renderTemplateValueHighlight(text);

  return (
    <article
      role={onNotificationClick ? 'button' : undefined}
      tabIndex={onNotificationClick ? 0 : undefined}
      onClick={onNotificationClick}
      onKeyDown={(event) => {
        if (!onNotificationClick) {
          return;
        }

        if (event.target !== event.currentTarget) {
          return;
        }

        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onNotificationClick();
        }
      }}
      className={`inbox-notification inbox-notification--${priority} ${
        isRead ? 'is-read' : 'is-unread'
      } ${onNotificationClick ? 'cursor-pointer' : ''}`}
    >
      {/* Top-right controls: read toggle + three-dots menu */}
      <div className="absolute right-2 top-2 flex items-center gap-0.5">
        {!hideReadToggle && !isRead ? (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onToggleRead?.();
            }}
            aria-label={t('Mark as read')}
            aria-pressed={false}
            className="inbox-notification__read-toggle"
          >
            <CheckOutlined className="inbox-notification__read-icon" />
          </button>
        ) : null}

        {onMuteType || onUnmuteType ? (
          <Dropdown
            trigger={['click']}
            placement="bottomRight"
            menu={{
              items: [
                isMuted
                  ? {
                      key: 'unmute',
                      icon: (
                        <NotificationsActiveIcon style={{ fontSize: 15 }} />
                      ),
                      label: t('Unmute {{type}}', {
                        type: translatedMuteLabel,
                      }),
                    }
                  : {
                      key: 'mute',
                      icon: <NotificationsOffIcon style={{ fontSize: 14 }} />,
                      label: t('Mute {{type}}', {
                        type: translatedMuteLabel,
                      }),
                    },
              ],
              onClick: ({ key }) => {
                if (key === 'mute') onMuteType?.();
                if (key === 'unmute') onUnmuteType?.();
              },
            }}
          >
            <button
              type="button"
              aria-label={t('More options')}
              onClick={(e) => e.stopPropagation()}
              className="flex h-6 w-6 items-center justify-center rounded-full border border-transparent text-gray-500 transition hover:border-gray-200 hover:text-gray-500"
            >
              <MoreOutlined className="text-sm" />
            </button>
          </Dropdown>
        ) : null}
      </div>

      <div className="flex gap-3 pr-8">
        {/* Icon */}
        <div className="inbox-notification__icon">
          {getNotificationIcon(triggerType, iconUrl)}
        </div>

        <div className="min-w-0 flex-1">
          {/* Header */}
          <p className="inbox-notification__type">
            {translatedTypeLabel}{' '}
            <span className="font-normal text-xs text-gray-400">
              •{' '}
              {isUpdated ? (
                <span className="inbox-notification__updated">
                  {t('Updated')}
                </span>
              ) : null}
              {timeAgo}
            </span>
            {originalTimeAgo ? (
              <span className="font-normal text-xs text-gray-400">
                {' '}
                ({t('From {{date}}', { date: originalTimeAgo })})
              </span>
            ) : null}
          </p>

          {/* Message */}
          <div className="inbox-notification__message">{message}</div>

          {/* Progress */}
          {typeof progress === 'number' ? (
            <div className="mt-2 max-w-[640px]">
              <div className="h-1.5 w-full rounded-full bg-gray-200">
                <div
                  className="inbox-notification__progress-bar h-1.5 rounded-full transition-all"
                  style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
                />
              </div>
              {progressDetail ? (
                <p className="mt-1 text-xs text-brand-gray">{progressDetail}</p>
              ) : null}
            </div>
          ) : null}

          {/* Detail */}
          {showDetailCard ? (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                if (!detailCardIsExpired) {
                  onPrimaryAction?.();
                }
              }}
              disabled={!showDetailAction}
              className={`inbox-notification__detail group ${
                showDetailAction ? 'is-clickable' : 'cursor-default'
              } ${detailCardIsExpired ? 'is-expired' : ''}`}
            >
              <span className="flex min-w-0 items-center gap-2">
                <span
                  className={`inbox-notification__detail-icon ${
                    showDetailAction ? 'is-clickable' : ''
                  } ${detailCardIsExpired ? 'is-expired' : ''}`}
                >
                  {getDetailCardIcon(
                    triggerType,
                    iconUrl,
                    exportFileType,
                    detailCardVariant
                  )}
                </span>
                <span className="min-w-0 truncate">{detailCardLabel}</span>
              </span>
              {detailCardIsExpired ? (
                <span className="inbox-notification__detail-expired">
                  {t('Expired')}
                </span>
              ) : null}
              {showDetailAction ? (
                <RightOutlined className="inbox-notification__detail-arrow is-clickable" />
              ) : null}
            </button>
          ) : null}

          {/* Quote */}
          {quoteText ? (
            <p className="inbox-notification__quote">
              {shouldAddUiQuotes ? '“' : null}
              {renderQuoteText(quoteText)}
              {shouldAddUiQuotes ? '”' : null}
            </p>
          ) : null}

          {/* Actions */}
          <div className="mt-2 flex gap-2">
            {primaryActionLabel &&
            (!showDetailAction || showPrimaryActionWithDetailCard) ? (
              <Button
                color={priority === 'high' ? 'danger' : 'primary'}
                className="inbox-notification__action inbox-notification__action--primary"
                onClick={(event) => {
                  event.stopPropagation();
                  onPrimaryAction?.();
                }}
              >
                {primaryActionLabel}
              </Button>
            ) : null}

            {secondaryActionLabel ? (
              <Button
                color={priority === 'high' ? 'danger' : 'default'}
                className="inbox-notification__action inbox-notification__action--secondary"
                onClick={(event) => {
                  event.stopPropagation();
                  onSecondaryAction?.();
                }}
              >
                {secondaryActionLabel}
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </article>
  );
};

export default InboxNotification;
